import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "@hole/shared/auth";

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout } = useAuth();

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/transparency-map", label: "Transparency Map" },
    { href: "/wiki", label: "Wiki" },
    { href: "/foia-generator", label: "FOIA Generator" },
  ];

  const footerLinks = [{ href: "/privacy", label: "Legal & Privacy" }];

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="w-full bg-brand-martinique border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-8 xl:px-[135px]">
          <div className="flex items-center justify-between h-[90px]">
            {/* Logo - white text for dark background */}
            <a href="/" className="flex items-center">
              <div className="flex items-center justify-center h-[50px]">
                <img
                  src="/assets/images/hole-logo-dark-bg.svg"
                  alt="The HOLE Truth"
                  className="w-auto h-[40px] flex-shrink-0"
                />
              </div>
            </a>

            {/* Desktop Menu */}
            <div className="hidden xl:flex items-center gap-10">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-white font-agency text-2xl font-bold hover:text-brand-orange transition-colors whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
              {footerLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-white font-agency text-2xl font-bold hover:text-brand-orange transition-colors whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Right Side: Login + Sign Up / User Menu */}
            <div className="hidden xl:flex items-center gap-[22px]">
              {isLoading ? (
                <span className="text-white/50 font-agency text-2xl">Loading...</span>
              ) : isAuthenticated ? (
                <>
                  <span className="text-white font-agency text-xl">
                    {user?.name || user?.email}
                  </span>
                  <button
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    className="px-6 py-2 bg-brand-orange text-white font-agency text-2xl font-bold rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => loginWithRedirect()}
                    className="text-white font-agency text-2xl font-bold hover:text-brand-orange transition-colors whitespace-nowrap"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => loginWithRedirect()}
                    className="px-6 py-2 bg-brand-orange text-white font-agency text-2xl font-bold rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 text-white hover:text-brand-orange transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 xl:hidden"
            onClick={closeMenu}
            aria-hidden="true"
          />

          {/* Menu Panel */}
          <div className="fixed top-[90px] left-0 right-0 bg-brand-martinique border-b border-white/10 z-50 xl:hidden">
            <div className="px-6 py-4 space-y-4 max-h-[calc(100vh-90px)] overflow-y-auto">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block py-2 text-white font-agency text-xl font-bold hover:text-brand-orange transition-colors"
                  onClick={closeMenu}
                >
                  {link.label}
                </a>
              ))}
              <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
                {footerLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block py-2 text-white font-agency text-xl font-bold hover:text-brand-orange transition-colors"
                    onClick={closeMenu}
                  >
                    {link.label}
                  </a>
                ))}
                <div className="flex items-center gap-3 pt-2">
                  {isLoading ? (
                    <span className="text-white/50 font-agency text-lg">Loading...</span>
                  ) : isAuthenticated ? (
                    <div className="flex-1 space-y-3">
                      <div className="text-white font-agency text-lg text-center">
                        {user?.name || user?.email}
                      </div>
                      <button
                        onClick={() => {
                          logout({ logoutParams: { returnTo: window.location.origin } });
                          closeMenu();
                        }}
                        className="w-full px-6 py-3 bg-brand-orange text-white font-agency text-xl font-bold rounded-md hover:bg-orange-600 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          loginWithRedirect();
                          closeMenu();
                        }}
                        className="flex-1 px-4 py-2 text-white font-agency text-lg font-bold border-2 border-brand-orange rounded-md hover:bg-white/10 transition-colors"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          loginWithRedirect();
                          closeMenu();
                        }}
                        className="flex-1 px-6 py-3 bg-brand-orange text-white font-agency text-xl font-bold rounded-md hover:bg-orange-600 transition-colors"
                      >
                        Sign Up
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
